import { useState, useEffect } from 'react';
import { PageLayoutsContainer } from '@wajeez-web/components/layouts';
import { Button } from '@wajeez-web/components/ui';
import { PlusIcon } from '@heroicons/react/solid';
import { SuperAdminSystemPermissions } from '@wajeez-web/core/types';
import { useNavigate } from 'react-router-dom';
import {
  useContentSelector,
  useContentDispatch,
  CollectionComponetsActions,
} from '@wajeez-web/stores/content';
import { HandleTableData } from '@wajeez-web/components/data-table';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { DotsHorizontalIcon } from '@heroicons/react/outline';
import { contentEntityDataType } from '@wajeez-web/stores/content';
import { DataTable, Dropdown, CheckBoxType } from '@wajeez-web/components/mui';

import { CloneModal } from '../components/CloneModal';

const CollectionComponentsPage = () => {
  const navigate = useNavigate();

  const contentDispatch = useContentDispatch();

  const [
    isCloneCollectionComponentModalOpen,
    setIsCloneCollectionComponentModalOpen,
  ] = useState(false);
  const [selectedCollectionComponentId, setSelectedCollectionComponentId] =
    useState<string>();
  const [
    isCloneCollectionComponentSaveAndEditSuccess,
    setIsCloneCollectionComponentSaveAndEditSuccess,
  ] = useState(false);

  const cloneCollectionComponent = useContentSelector(
    ({ collectionComponents }) => collectionComponents.cloneCollectionComponent
  );

  const {
    tablePreferances,
    setTablePreferences,
    getData: getDatatableData,
    isTableReferencesLoading,
  } = HandleTableData(
    'collectionComponents',
    CollectionComponetsActions.getCollectionComponentDatatableAsync,
    undefined,
    undefined,
    [{ property: 1, orderByDirection: 1 }]
  );

  const dataTableData = useContentSelector(
    ({ collectionComponents }) =>
      collectionComponents.collectionComponentsDatatable
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (
      isCloneCollectionComponentSaveAndEditSuccess &&
      cloneCollectionComponent.data
    ) {
      navigate(`/collection-components/${cloneCollectionComponent.data}/edit`);
    }
  }, [
    isCloneCollectionComponentSaveAndEditSuccess,
    cloneCollectionComponent.data,
  ]);

  return (
    <>
      <PageLayoutsContainer
        title={`Collection Components`}
        endSideComponent={[
          {
            element: (
              <Button
                title={'Add Collection Component'}
                Icon={PlusIcon}
                onClick={() => {
                  navigate('/collection-components/new');
                }}
                bg="primary"
                color="white"
              />
            ),
            permission: [SuperAdminSystemPermissions.collectionComponents.add],
          },
        ]}
        breadCrumbsLinks={[{ title: '' }]}
      >
        <DataTable
          dataTableData={dataTableData.data as contentEntityDataType}
          onHeaderManage={(header) => {
            const flags = header
              ?.filter((h) => h.selected)
              .reduce(
                (previousValue: number, currentValue: CheckBoxType) =>
                  previousValue + Number(currentValue.value),
                0
              );

            tablePreferances &&
              flags &&
              setTablePreferences({
                ...tablePreferances,
                flags: flags + 1,
              });
          }}
          onPaginationChange={(pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
              });
          }}
          sort={tablePreferances?.sortingOperations?.[0]}
          onSort={(sort) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                sortingOperations: [sort],
              });
          }}
          onFilter={(filters, pagination) => {
            tablePreferances &&
              setTablePreferences({
                ...tablePreferances,
                pageNumber: pagination.pageNumber,
                filteringOperations: filters.map((f) => {
                  return {
                    property: Number(f.key),
                    cretiria: f.value,
                    comparisonType: f.operator,
                  };
                }),
              });
          }}
          renderRowActions={(row) => {
            return (
              <div className="w-full flex justify-center items-center">
                <Dropdown
                  id={`action-${row[1]}`}
                  items={[
                    {
                      label: 'Content',
                      onClick: () => {
                        navigate(`/collection-components/${row[1]}`);
                      },
                    },
                    {
                      label: 'Edit',
                      onClick: () => {
                        navigate(`/collection-components/${row[1]}/edit`);
                      },
                    },
                    {
                      label: 'Clone',
                      onClick: () => {
                        setSelectedCollectionComponentId(row[1] as string);
                        setIsCloneCollectionComponentModalOpen(true);
                      },
                    },
                  ]}
                  label={
                    <div style={{ color: '#343a40' }}>
                      <DotsHorizontalIcon
                        style={{ width: '28px', height: '24px' }}
                      />
                    </div>
                  }
                  buttonStyle={{
                    width: '34px',
                    minWidth: '34px',
                    paddingRight: '2px',
                    paddingLeft: '2px',
                    color: 'black',
                  }}
                />
              </div>
            );
          }}
          isLoading={dataTableData.status === 'loading'}
        />
      </PageLayoutsContainer>
      {isCloneCollectionComponentModalOpen && (
        <CloneModal
          isOpen={isCloneCollectionComponentModalOpen}
          onClose={() => {
            setIsCloneCollectionComponentModalOpen(false);
          }}
          title="Clone Collection Component"
          onSave={async (name) => {
            const res = await contentDispatch(
              CollectionComponetsActions.cloneCollectionComponentAsync({
                referenceName: name,
                id: selectedCollectionComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneCollectionComponentModalOpen(false);

              tablePreferances && setTablePreferences({ ...tablePreferances });
            }
          }}
          onSaveAndEdit={async (name) => {
            const res = await contentDispatch(
              CollectionComponetsActions.cloneCollectionComponentAsync({
                referenceName: name,
                id: selectedCollectionComponentId,
              })
            );

            if (res.meta.requestStatus === 'fulfilled') {
              setIsCloneCollectionComponentSaveAndEditSuccess(true);
            }
          }}
          isLoading={cloneCollectionComponent.status === 'loading'}
        />
      )}
    </>
  );
};

export default CollectionComponentsPage;
